import { useState, useMemo } from "react";
import { Link } from "wouter";
import AdminLayout from "@/components/layout-admin";
import {
  useAdminFinancialRecords,
  useAdminFinancialSummary,
  useCreateFinancialRecord,
  useUpdateFinancialRecord,
  useDeleteFinancialRecord,
  useCommissionSettings,
  useUpdateCommissionSettings,
  useApartments,
  useAdminUsers,
} from "@/hooks/use-resources";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RecurringChargesTab } from "@/components/recurring-charges-tab";
import { RevertFinancialRecordDialog } from "@/components/revert-financial-record-dialog";
import { useAuth } from "@/hooks/use-auth";
import {
  DollarSign,
  TrendingUp,
  Users,
  Clock,
  Plus,
  Pencil,
  CheckCircle,
  XCircle,
  Receipt,
  Trash2,
  Search,
  ExternalLink,
  RotateCcw,
  TrendingDown,
  Wallet,
} from "lucide-react";
import type { FinancialRecord, Apartment } from "@shared/routes";

type FilterType = "all" | "check_in" | "cleaning" | "intra_stay_cleaning" | "unit_setup" | "maintenance" | "miscellaneous" | "credit";
type FilterStatus = "all" | "pending" | "paid" | "cancelled";

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function AdminFinances() {
  const { data: records = [], isLoading: recordsLoading } = useAdminFinancialRecords();
  const { data: summary, isLoading: summaryLoading } = useAdminFinancialSummary();
  const { data: settings, isLoading: settingsLoading } = useCommissionSettings();
  const { data: apartments = [] } = useApartments();
  const { data: usersData = [] } = useAdminUsers();
  const createRecord = useCreateFinancialRecord();
  const updateRecord = useUpdateFinancialRecord();
  const deleteRecord = useDeleteFinancialRecord();
  const updateSettings = useUpdateCommissionSettings();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = (currentUser as any)?.role === "super_admin";

  const [newTierBedrooms, setNewTierBedrooms] = useState("");
  const [newTierFee, setNewTierFee] = useState("");
  const [newTierPay, setNewTierPay] = useState("");

  const addTierMutation = useMutation({
    mutationFn: (data: { bedrooms: number; cleaningFee: string; cleanerPay: string }) =>
      apiRequest("POST", "/api/admin/commission-tiers", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/commission-settings"] });
      setNewTierBedrooms("");
      setNewTierFee("");
      setNewTierPay("");
      toast({ title: "Tier added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteTierMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/commission-tiers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/commission-settings"] });
      toast({ title: "Tier removed" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const [filterType, setFilterType] = useState<FilterType>("all");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FinancialRecord | null>(null);
  const [revertRecordId, setRevertRecordId] = useState<number | null>(null);
  const [deleteConfirmRecord, setDeleteConfirmRecord] = useState<FinancialRecord | null>(null);
  const [deleteConfirmTierId, setDeleteConfirmTierId] = useState<number | null>(null);

  const [editForm, setEditForm] = useState({
    type: "" as string,
    totalAmount: "",
    adminAmount: "",
    cleanerAmount: "",
    partnerAmount: "",
    description: "",
    status: "" as string,
    paymentMethod: "" as string,
    ownerUserId: "",
    cleanerUserId: "",
  });

  const [createForm, setCreateForm] = useState({
    type: "check_in" as "check_in" | "cleaning" | "intra_stay_cleaning" | "unit_setup" | "maintenance" | "miscellaneous" | "credit",
    apartmentId: "",
    cleanerUserId: "",
    ownerUserId: "",
    description: "",
    createdAt: "",
    totalAmount: "",
  });

  const [settingsForm, setSettingsForm] = useState({
    checkInFee: "",
    cleaningFee: "",
    cleanerPay: "",
    cleaningFeeStudio: "",
    cleanerPayStudio: "",
    cleaningFee2br: "",
    cleanerPay2br: "",
    cleaningFee3br: "",
    cleanerPay3br: "",
    cleaningFee4br: "",
    cleanerPay4br: "",
    cleaningFee5br: "",
    cleanerPay5br: "",
    partnerCommissionPct: "",
    partnerName: "",
    intraStayFee: "",
    intraStayCleanerPay: "",
  });

  const typedRecords = records as FinancialRecord[];
  const typedApartments = apartments as Apartment[];
  const typedUsers = usersData as Array<{ id: number; username: string; fullName?: string; role: string }>;

  const getApartmentName = (apartmentId: number | null | undefined) => {
    if (!apartmentId) return "N/A";
    const apt = typedApartments.find((a) => a.id === apartmentId);
    return apt?.name || `Apartment #${apartmentId}`;
  };

  const filteredRecords = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return typedRecords
      .filter((r) => {
        if (filterType !== "all" && r.type !== filterType) return false;
        if (filterStatus !== "all" && r.status !== filterStatus) return false;
        if (lowerSearch) {
          const aptName = getApartmentName(r.apartmentId).toLowerCase();
          const desc = (r.description || "").toLowerCase();
          if (!aptName.includes(lowerSearch) && !desc.includes(lowerSearch)) return false;
        }
        if (dateFrom) {
          const recordDate = r.createdAt ? new Date(r.createdAt) : null;
          if (!recordDate || recordDate < new Date(dateFrom)) return false;
        }
        if (dateTo) {
          const recordDate = r.createdAt ? new Date(r.createdAt) : null;
          const endDate = new Date(dateTo);
          endDate.setHours(23, 59, 59, 999);
          if (!recordDate || recordDate > endDate) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [typedRecords, filterType, filterStatus, searchTerm, dateFrom, dateTo]);

  const handleCreateRecord = async () => {
    if (!createForm.apartmentId) {
      toast({ title: "Error", description: "Please select an apartment", variant: "destructive" });
      return;
    }
    if ((createForm.type === "maintenance" || createForm.type === "miscellaneous") && (!createForm.totalAmount || Number(createForm.totalAmount) <= 0)) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      await createRecord.mutateAsync({
        type: createForm.type,
        apartmentId: Number(createForm.apartmentId),
        cleanerUserId: createForm.cleanerUserId && createForm.cleanerUserId !== "none" ? Number(createForm.cleanerUserId) : undefined,
        ownerUserId: createForm.ownerUserId && createForm.ownerUserId !== "none" ? Number(createForm.ownerUserId) : undefined,
        totalAmount: createForm.totalAmount ? Number(createForm.totalAmount) : undefined,
        description: createForm.description || undefined,
        createdAt: createForm.createdAt || undefined,
      });
      setCreateForm({ type: "check_in", apartmentId: "", cleanerUserId: "", ownerUserId: "", description: "", createdAt: "", totalAmount: "" });
      setCreateOpen(false);
      toast({ title: "Financial record created" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDeleteRecord = async (id: number) => {
    try {
      await deleteRecord.mutateAsync(id);
      toast({ title: "Financial record deleted" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleUpdateStatus = async (id: number, status: "paid" | "cancelled") => {
    try {
      await updateRecord.mutateAsync({ id, data: { status } });
      toast({ title: `Record marked as ${status}` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleOpenEdit = (record: FinancialRecord) => {
    setEditingRecord(record);
    setEditForm({
      type: record.type || "cleaning",
      totalAmount: record.totalAmount || "0.00",
      adminAmount: record.adminAmount || "0.00",
      cleanerAmount: record.cleanerAmount || "0.00",
      partnerAmount: record.partnerAmount || "0.00",
      description: record.description || "",
      status: record.status || "pending",
      paymentMethod: record.paymentMethod || "",
      ownerUserId: record.ownerUserId ? String(record.ownerUserId) : "none",
      cleanerUserId: record.cleanerUserId ? String(record.cleanerUserId) : "none",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    try {
      const data: any = {};
      if (editForm.type !== editingRecord.type) data.type = editForm.type;
      if (editForm.totalAmount !== (editingRecord.totalAmount || "0.00")) data.totalAmount = editForm.totalAmount;
      if (editForm.adminAmount !== (editingRecord.adminAmount || "0.00")) data.adminAmount = editForm.adminAmount;
      if (editForm.cleanerAmount !== (editingRecord.cleanerAmount || "0.00")) data.cleanerAmount = editForm.cleanerAmount;
      if (editForm.partnerAmount !== (editingRecord.partnerAmount || "0.00")) data.partnerAmount = editForm.partnerAmount;
      if (editForm.description !== (editingRecord.description || "")) data.description = editForm.description;
      if (editForm.status !== (editingRecord.status || "pending")) data.status = editForm.status;
      if (editForm.paymentMethod && editForm.paymentMethod !== (editingRecord.paymentMethod || "")) data.paymentMethod = editForm.paymentMethod;
      const newOwnerId = editForm.ownerUserId === "none" ? null : Number(editForm.ownerUserId);
      if (newOwnerId !== (editingRecord.ownerUserId || null)) data.ownerUserId = newOwnerId;
      const newCleanerId = editForm.cleanerUserId === "none" ? null : Number(editForm.cleanerUserId);
      if (newCleanerId !== (editingRecord.cleanerUserId || null)) data.cleanerUserId = newCleanerId;

      if (Object.keys(data).length === 0) {
        setEditOpen(false);
        return;
      }

      await updateRecord.mutateAsync({ id: editingRecord.id, data });
      setEditOpen(false);
      setEditingRecord(null);
      toast({ title: "Financial record updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleOpenSettings = () => {
    if (settings) {
      setSettingsForm({
        checkInFee: settings.checkInFee || "50.00",
        cleaningFee: settings.cleaningFee || "110.00",
        cleanerPay: settings.cleanerPay || "75.00",
        cleaningFeeStudio: settings.cleaningFeeStudio || "90.00",
        cleanerPayStudio: settings.cleanerPayStudio || "60.00",
        cleaningFee2br: settings.cleaningFee2br || "140.00",
        cleanerPay2br: settings.cleanerPay2br || "95.00",
        cleaningFee3br: settings.cleaningFee3br || "170.00",
        cleanerPay3br: settings.cleanerPay3br || "115.00",
        cleaningFee4br: settings.cleaningFee4br || "200.00",
        cleanerPay4br: settings.cleanerPay4br || "140.00",
        cleaningFee5br: settings.cleaningFee5br || "240.00",
        cleanerPay5br: settings.cleanerPay5br || "165.00",
        partnerCommissionPct: settings.partnerCommissionPct || "14.00",
        partnerName: settings.partnerName || "Partner",
        intraStayFee: (settings as any).intraStayFee || "80.00",
        intraStayCleanerPay: (settings as any).intraStayCleanerPay || "60.00",
      });
    }
    setSettingsOpen(true);
  };

  const handleUpdateSettings = async () => {
    try {
      await updateSettings.mutateAsync({
        checkInFee: settingsForm.checkInFee,
        cleaningFee: settingsForm.cleaningFee,
        cleanerPay: settingsForm.cleanerPay,
        cleaningFeeStudio: settingsForm.cleaningFeeStudio,
        cleanerPayStudio: settingsForm.cleanerPayStudio,
        cleaningFee2br: settingsForm.cleaningFee2br,
        cleanerPay2br: settingsForm.cleanerPay2br,
        cleaningFee3br: settingsForm.cleaningFee3br,
        cleanerPay3br: settingsForm.cleanerPay3br,
        cleaningFee4br: settingsForm.cleaningFee4br,
        cleanerPay4br: settingsForm.cleanerPay4br,
        cleaningFee5br: settingsForm.cleaningFee5br,
        cleanerPay5br: settingsForm.cleanerPay5br,
        partnerCommissionPct: settingsForm.partnerCommissionPct,
        partnerName: settingsForm.partnerName,
        intraStayFee: settingsForm.intraStayFee,
        intraStayCleanerPay: settingsForm.intraStayCleanerPay,
      });
      setSettingsOpen(false);
      toast({ title: "Commission settings updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const isLoading = recordsLoading || summaryLoading || settingsLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-finances-title">
              Finances
            </h1>
            <p className="text-muted-foreground">
              Revenue & Commission Tracking
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => setCreateForm({ type: "check_in", apartmentId: "", cleanerUserId: "", ownerUserId: "", description: "", createdAt: "", totalAmount: "" })}
                data-testid="button-create-record"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Record
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Financial Record</DialogTitle>
                <DialogDescription>
                  Manually create a new financial record. Amounts will be auto-calculated from commission settings.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={createForm.type}
                    onValueChange={(v) => setCreateForm((p) => ({ ...p, type: v as "check_in" | "cleaning" | "intra_stay_cleaning" | "unit_setup" | "maintenance" | "miscellaneous" | "credit" }))}
                  >
                    <SelectTrigger data-testid="select-record-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check_in">Check-in Fee</SelectItem>
                      <SelectItem value="cleaning">Cleaning Fee</SelectItem>
                      <SelectItem value="intra_stay_cleaning">Mid-Stay Cleaning</SelectItem>
                      <SelectItem value="unit_setup">Unit Initial Setup ($200)</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                      <SelectItem value="credit">Credit (Correction)</SelectItem>
                    </SelectContent>
                  </Select>
                  {createForm.type === "credit" && (
                    <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1 mt-1">
                      Credits reduce what the property manager owes. Enter the amount to credit.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Apartment</Label>
                  <SearchableSelect
                    value={createForm.apartmentId}
                    onValueChange={(v) => {
                      const selectedApt = typedApartments.find(a => a.id === Number(v));
                      setCreateForm((p) => ({
                        ...p,
                        apartmentId: v,
                        ownerUserId: selectedApt?.ownerUserId ? String(selectedApt.ownerUserId) : "none",
                      }));
                    }}
                    placeholder="Select apartment"
                    searchPlaceholder="Search apartment..."
                    options={typedApartments.map((a) => ({ value: String(a.id), label: a.name }))}
                    className="w-full"
                    data-testid="select-record-apartment"
                  />
                </div>
                <div>
                  <Label>Property Manager</Label>
                  <SearchableSelect
                    value={createForm.ownerUserId}
                    onValueChange={(v) => setCreateForm((p) => ({ ...p, ownerUserId: v }))}
                    placeholder="Select property manager"
                    searchPlaceholder="Search PM..."
                    options={[
                      { value: "none", label: "No Property Manager" },
                      ...typedUsers.filter(u => u.role === "owner").map((u) => ({ value: String(u.id), label: `${u.fullName || u.username}${(u as any).isActive === false ? " (inactive)" : ""}` })),
                    ]}
                    className="w-full"
                    data-testid="select-record-owner"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Auto-filled from apartment. Change if needed.</p>
                </div>
                {createForm.type === "cleaning" && (
                  <div>
                    <Label>Cleaner Crew</Label>
                    <SearchableSelect
                      value={createForm.cleanerUserId}
                      onValueChange={(v) => setCreateForm((p) => ({ ...p, cleanerUserId: v }))}
                      placeholder="Select cleaner (optional)"
                      searchPlaceholder="Search cleaner..."
                      options={[
                        { value: "none", label: "No Cleaner" },
                        ...typedUsers.filter(u => u.role === "cleaner" || ((u as any).additionalRoles as string[] | undefined ?? []).includes("cleaner")).map((u) => ({ value: String(u.id), label: `${u.fullName || u.username}${(u as any).isActive === false ? " (inactive)" : ""}` })),
                      ]}
                      className="w-full"
                      data-testid="select-record-cleaner"
                    />
                  </div>
                )}
                {(createForm.type === "maintenance" || createForm.type === "miscellaneous" || createForm.type === "credit" || createForm.type === "intra_stay_cleaning") && (
                  <div>
                    <Label>Amount ($){createForm.type === "credit" ? " to Credit" : createForm.type === "intra_stay_cleaning" ? " (overrides default rate)" : ""}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={createForm.totalAmount}
                      onChange={(e) => setCreateForm((p) => ({ ...p, totalAmount: e.target.value }))}
                      placeholder={createForm.type === "credit" ? "Credit amount" : createForm.type === "intra_stay_cleaning" ? "Leave blank for default rate" : "Enter amount"}
                      data-testid="input-record-amount"
                    />
                  </div>
                )}
                {createForm.type === "intra_stay_cleaning" && (
                  <div>
                    <Label>Assign Cleaner</Label>
                    <SearchableSelect
                      value={createForm.cleanerUserId}
                      onValueChange={(v) => setCreateForm((p) => ({ ...p, cleanerUserId: v }))}
                      placeholder="Select cleaner (optional)"
                      searchPlaceholder="Search cleaner..."
                      options={[
                        { value: "none", label: "No Cleaner" },
                        ...typedUsers.filter(u => u.role === "cleaner" || ((u as any).additionalRoles as string[] | undefined ?? []).includes("cleaner")).map((u) => ({ value: String(u.id), label: `${u.fullName || u.username}${(u as any).isActive === false ? " (inactive)" : ""}` })),
                      ]}
                      className="w-full"
                      data-testid="select-record-cleaner-intra"
                    />
                  </div>
                )}
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={createForm.createdAt}
                    onChange={(e) => setCreateForm((p) => ({ ...p, createdAt: e.target.value }))}
                    data-testid="input-record-date"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to use today's date</p>
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    value={createForm.description}
                    onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Add a note..."
                    data-testid="input-record-description"
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handleCreateRecord}
                  disabled={createRecord.isPending}
                  data-testid="button-submit-record"
                >
                  {createRecord.isPending ? "Creating..." : "Create Record"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="text-loading">
            Loading financial data...
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Total Revenue</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-total-revenue">
                    {formatMoney(summary?.totalRevenue)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Admin Commission</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-admin-commission">
                    {formatMoney(summary?.totalAdminCommission)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Gross, before org expenses</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <span className="text-xs text-muted-foreground">Org Expenses</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700" data-testid="text-org-expenses">
                    {formatMoney(summary?.totalOrgExpenses)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Utilities, salaries, absorbed costs</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-muted-foreground">Net Profit</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="text-net-profit">
                    {formatMoney(summary?.netAdminProfit)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Commission − org expenses</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Cleaner Payments</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-cleaner-payments">
                    {formatMoney(summary?.totalCleanerPayments)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Receipt className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Check-in Fees</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-checkin-fees">
                    {formatMoney(summary?.totalCheckInFees)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Receipt className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Cleaning Fees</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-cleaning-fees">
                    {formatMoney(summary?.totalCleaningFees)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Pending Payments</span>
                  </div>
                  <p className="text-2xl font-bold" data-testid="text-pending-payments">
                    {formatMoney(summary?.pendingPayments)}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="records" className="space-y-4">
              <TabsList>
                <TabsTrigger value="records">Records</TabsTrigger>
                <TabsTrigger value="recurring">Recurring</TabsTrigger>
              </TabsList>

              <TabsContent value="recurring">
                <RecurringChargesTab />
              </TabsContent>

              <TabsContent value="records" className="space-y-6">

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-base font-display">Commission Settings</CardTitle>
                <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenSettings}
                      data-testid="button-edit-settings"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Rates
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update Commission Settings</DialogTitle>
                      <DialogDescription>
                        Adjust the fee rates for check-ins and cleaning services.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                      <div>
                        <Label>Check-in Fee ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={settingsForm.checkInFee}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, checkInFee: e.target.value }))}
                          data-testid="input-checkin-fee"
                        />
                      </div>
                      <Separator />
                      <p className="text-sm font-medium">Cleaning Fees by Apartment Type</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Studio - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFeeStudio} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFeeStudio: e.target.value }))} data-testid="input-cleaning-fee-studio" />
                        </div>
                        <div>
                          <Label className="text-xs">Studio - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPayStudio} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPayStudio: e.target.value }))} data-testid="input-cleaner-pay-studio" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">1 BR - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFee} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFee: e.target.value }))} data-testid="input-cleaning-fee" />
                        </div>
                        <div>
                          <Label className="text-xs">1 BR - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPay} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPay: e.target.value }))} data-testid="input-cleaner-pay" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">2 BR - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFee2br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFee2br: e.target.value }))} data-testid="input-cleaning-fee-2br" />
                        </div>
                        <div>
                          <Label className="text-xs">2 BR - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPay2br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPay2br: e.target.value }))} data-testid="input-cleaner-pay-2br" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">3 BR - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFee3br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFee3br: e.target.value }))} data-testid="input-cleaning-fee-3br" />
                        </div>
                        <div>
                          <Label className="text-xs">3 BR - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPay3br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPay3br: e.target.value }))} data-testid="input-cleaner-pay-3br" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">4 BR - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFee4br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFee4br: e.target.value }))} data-testid="input-cleaning-fee-4br" />
                        </div>
                        <div>
                          <Label className="text-xs">4 BR - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPay4br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPay4br: e.target.value }))} data-testid="input-cleaner-pay-4br" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">5+ BR - Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleaningFee5br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleaningFee5br: e.target.value }))} data-testid="input-cleaning-fee-5br" />
                        </div>
                        <div>
                          <Label className="text-xs">5+ BR - Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.cleanerPay5br} onChange={(e) => setSettingsForm((p) => ({ ...p, cleanerPay5br: e.target.value }))} data-testid="input-cleaner-pay-5br" />
                        </div>
                      </div>
                      <Separator />
                      <p className="text-sm font-medium">Custom Bedroom Tiers (6+ BR)</p>
                      <p className="text-xs text-muted-foreground">These override the fixed rates above for any bedroom count ≥ 6.</p>
                      {((settings as any)?.tiers ?? []).length > 0 && (
                        <div className="rounded border divide-y text-sm">
                          <div className="grid grid-cols-4 gap-2 px-3 py-1 text-xs font-medium text-muted-foreground">
                            <span>BR</span><span>Fee</span><span>Pay</span><span />
                          </div>
                          {((settings as any)?.tiers ?? []).map((t: any) => (
                            <div key={t.id} className="grid grid-cols-4 gap-2 items-center px-3 py-1.5" data-testid={`row-tier-${t.id}`}>
                              <span className="font-medium">{t.bedrooms} BR</span>
                              <span>${Number(t.cleaningFee).toFixed(2)}</span>
                              <span>${Number(t.cleanerPay).toFixed(2)}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                onClick={() => setDeleteConfirmTierId(t.id)}
                                disabled={deleteTierMutation.isPending}
                                data-testid={`button-delete-tier-${t.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-4 gap-2 items-end">
                        <div>
                          <Label className="text-xs">BR #</Label>
                          <Input type="number" min="6" placeholder="6" value={newTierBedrooms} onChange={(e) => setNewTierBedrooms(e.target.value)} data-testid="input-new-tier-bedrooms" />
                        </div>
                        <div>
                          <Label className="text-xs">Fee ($)</Label>
                          <Input type="number" step="0.01" placeholder="200.00" value={newTierFee} onChange={(e) => setNewTierFee(e.target.value)} data-testid="input-new-tier-fee" />
                        </div>
                        <div>
                          <Label className="text-xs">Pay ($)</Label>
                          <Input type="number" step="0.01" placeholder="140.00" value={newTierPay} onChange={(e) => setNewTierPay(e.target.value)} data-testid="input-new-tier-pay" />
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (!newTierBedrooms || !newTierFee || !newTierPay) return;
                            addTierMutation.mutate({ bedrooms: Number(newTierBedrooms), cleaningFee: newTierFee, cleanerPay: newTierPay });
                          }}
                          disabled={addTierMutation.isPending || !newTierBedrooms || !newTierFee || !newTierPay}
                          data-testid="button-add-tier"
                        >
                          <Plus className="h-4 w-4 mr-1" />Add
                        </Button>
                      </div>
                      <Separator />
                      <p className="text-sm font-medium">Mid-Stay Cleaning</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Mid-Stay Fee ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.intraStayFee} onChange={(e) => setSettingsForm((p) => ({ ...p, intraStayFee: e.target.value }))} data-testid="input-intra-stay-fee" />
                        </div>
                        <div>
                          <Label className="text-xs">Mid-Stay Cleaner Pay ($)</Label>
                          <Input type="number" step="0.01" value={settingsForm.intraStayCleanerPay} onChange={(e) => setSettingsForm((p) => ({ ...p, intraStayCleanerPay: e.target.value }))} data-testid="input-intra-stay-cleaner-pay" />
                        </div>
                      </div>
                      <Separator />
                      <p className="text-sm font-medium">Partner Commission</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Partner Name</Label>
                          <Input value={settingsForm.partnerName} onChange={(e) => setSettingsForm((p) => ({ ...p, partnerName: e.target.value }))} placeholder="Partner" data-testid="input-partner-name" />
                        </div>
                        <div>
                          <Label className="text-xs">Commission (%)</Label>
                          <Input type="number" step="0.01" value={settingsForm.partnerCommissionPct} onChange={(e) => setSettingsForm((p) => ({ ...p, partnerCommissionPct: e.target.value }))} data-testid="input-partner-pct" />
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        onClick={handleUpdateSettings}
                        disabled={updateSettings.isPending}
                        data-testid="button-save-settings"
                      >
                        {updateSettings.isPending ? "Saving..." : "Save Settings"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {settingsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading settings...</p>
                ) : settings ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Check-in Fee</p>
                        <p className="text-lg font-bold" data-testid="text-setting-checkin-fee">
                          {formatMoney(settings.checkInFee)}
                        </p>
                      </div>
                    </div>
                    <Separator />
                    <p className="text-xs text-muted-foreground font-medium">Cleaning Rates by Apartment Type</p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Studio</p>
                        <p className="text-sm font-bold" data-testid="text-setting-cleaning-studio">
                          {formatMoney(settings.cleaningFeeStudio)} / {formatMoney(settings.cleanerPayStudio)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">1 BR</p>
                        <p className="text-sm font-bold" data-testid="text-setting-cleaning-fee">
                          {formatMoney(settings.cleaningFee)} / {formatMoney(settings.cleanerPay)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">2 BR</p>
                        <p className="text-sm font-bold" data-testid="text-setting-cleaning-2br">
                          {formatMoney(settings.cleaningFee2br)} / {formatMoney(settings.cleanerPay2br)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">3 BR</p>
                        <p className="text-sm font-bold" data-testid="text-setting-cleaning-3br">
                          {formatMoney(settings.cleaningFee3br)} / {formatMoney(settings.cleanerPay3br)}
                        </p>
                      </div>
                      {((settings as any)?.tiers ?? []).map((t: any) => (
                        <div key={t.id}>
                          <p className="text-xs text-muted-foreground">{t.bedrooms} BR</p>
                          <p className="text-sm font-bold" data-testid={`text-setting-cleaning-${t.bedrooms}br`}>
                            {formatMoney(t.cleaningFee)} / {formatMoney(t.cleanerPay)}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Fee / Cleaner Pay</p>
                    <Separator />
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Partner</p>
                        <p className="text-sm font-bold" data-testid="text-setting-partner">
                          {settings.partnerName || "Partner"} ({settings.partnerCommissionPct || "14"}%)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No commission settings configured.</p>
                )}
              </CardContent>
            </Card>

            <Separator />

            <div>
              <h2 className="font-display text-lg font-semibold mb-3" data-testid="text-transactions-title">
                Transaction History
              </h2>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search description, apartment..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" data-testid="search-input" />
                </div>
                <Select value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
                  <SelectTrigger className="w-[150px]" data-testid="select-filter-type">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="check_in">Check-in</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="intra_stay_cleaning">Mid-Stay Cleaning</SelectItem>
                    <SelectItem value="unit_setup">Unit Setup</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
                  <SelectTrigger className="w-[150px]" data-testid="select-filter-status">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" data-testid="input-date-from" />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" data-testid="input-date-to" />
              </div>

              {filteredRecords.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p data-testid="text-empty-records">No financial records found.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredRecords.map((record) => (
                    <Card key={record.id} data-testid={`card-record-${record.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                className={
                                  record.type === "check_in"
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-default-hover-elevate no-default-active-elevate"
                                    : record.type === "unit_setup"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 no-default-hover-elevate no-default-active-elevate"
                                    : record.type === "maintenance"
                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 no-default-hover-elevate no-default-active-elevate"
                                    : record.type === "miscellaneous"
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 no-default-hover-elevate no-default-active-elevate"
                                    : record.type === "intra_stay_cleaning"
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 no-default-hover-elevate no-default-active-elevate"
                                    : record.type === "credit"
                                    ? "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 no-default-hover-elevate no-default-active-elevate"
                                    : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate"
                                }
                                data-testid={`badge-type-${record.id}`}
                              >
                                {record.type === "check_in" ? "Check-in" : record.type === "unit_setup" ? "Unit Setup" : record.type === "maintenance" ? "Maintenance" : record.type === "miscellaneous" ? "Miscellaneous" : record.type === "intra_stay_cleaning" ? "Mid-Stay Clean" : record.type === "credit" ? "Credit" : "Cleaning"}
                              </Badge>
                              {record.status === "pending" && (
                                <Badge variant="default" data-testid={`badge-status-${record.id}`}>
                                  Pending
                                </Badge>
                              )}
                              {record.status === "paid" && (
                                <Badge
                                  className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate"
                                  data-testid={`badge-status-${record.id}`}
                                >
                                  Paid
                                </Badge>
                              )}
                              {record.status === "cancelled" && (
                                <Badge variant="destructive" data-testid={`badge-status-${record.id}`}>
                                  Cancelled
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium" data-testid={`text-apartment-${record.id}`}>
                              {getApartmentName(record.apartmentId)}
                              {record.ownerUserId && (
                                <span className="text-muted-foreground font-normal ml-2">
                                  {(() => {
                                    const owner = typedUsers.find(u => u.id === record.ownerUserId);
                                    return owner ? `(${owner.fullName || owner.username})` : "";
                                  })()}
                                </span>
                              )}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <span data-testid={`text-total-${record.id}`}>
                                Total: {formatMoney(record.totalAmount)}
                              </span>
                              <span data-testid={`text-admin-${record.id}`}>
                                Admin: {formatMoney(record.adminAmount)}
                              </span>
                              <span data-testid={`text-cleaner-${record.id}`}>
                                Cleaner: {formatMoney(record.cleanerAmount)}
                              </span>
                            </div>
                            {record.description && (
                              <p className="text-xs text-muted-foreground" data-testid={`text-description-${record.id}`}>
                                {record.description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              <p className="text-xs text-muted-foreground" data-testid={`text-date-${record.id}`}>
                                {record.createdAt ? formatDate(record.createdAt) : "N/A"}
                              </p>
                              {(record as any).cleaningJobId && (
                                <Link
                                  href="/admin/cleaning-jobs"
                                  className="text-xs text-primary hover:underline flex items-center gap-0.5"
                                  data-testid={`link-cleaning-job-${record.id}`}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Job #{(record as any).cleaningJobId}
                                </Link>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            {record.status === "pending" && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUpdateStatus(record.id, "paid")}
                                  disabled={updateRecord.isPending}
                                  data-testid={`button-mark-paid-${record.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Paid
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUpdateStatus(record.id, "cancelled")}
                                  disabled={updateRecord.isPending}
                                  data-testid={`button-mark-cancelled-${record.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                              </>
                            )}
                            {record.status === "paid" && isSuperAdmin && (
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setRevertRecordId(record.id)}
                                title="Revertir pago"
                                data-testid={`button-revert-${record.id}`}
                              >
                                <RotateCcw className="w-4 h-4 text-amber-600" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleOpenEdit(record)}
                              data-testid={`button-edit-${record.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setDeleteConfirmRecord(record)}
                              disabled={deleteRecord.isPending}
                              data-testid={`button-delete-${record.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

              </TabsContent>
            </Tabs>

            <RevertFinancialRecordDialog
              open={revertRecordId !== null}
              recordId={revertRecordId}
              onOpenChange={(open) => { if (!open) setRevertRecordId(null); }}
            />

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Financial Record</DialogTitle>
                  <DialogDescription>
                    {editingRecord ? `Record #${editingRecord.id} - ${getApartmentName(editingRecord.apartmentId)}` : ""}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={editForm.type} onValueChange={(v) => setEditForm((p) => ({ ...p, type: v }))}>
                        <SelectTrigger data-testid="select-edit-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="check_in">Check-in Fee</SelectItem>
                          <SelectItem value="cleaning">Cleaning Fee</SelectItem>
                          <SelectItem value="intra_stay_cleaning">Mid-Stay Cleaning</SelectItem>
                          <SelectItem value="unit_setup">Unit Setup</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                          <SelectItem value="booking_commission">Booking Commission</SelectItem>
                          <SelectItem value="credit">Credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={editForm.status} onValueChange={(v) => setEditForm((p) => ({ ...p, status: v }))}>
                        <SelectTrigger data-testid="select-edit-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Total Amount ($)</Label>
                      <Input type="number" step="0.01" value={editForm.totalAmount} onChange={(e) => setEditForm((p) => ({ ...p, totalAmount: e.target.value }))} data-testid="input-edit-total" />
                    </div>
                    <div>
                      <Label className="text-xs">Admin Amount ($)</Label>
                      <Input type="number" step="0.01" value={editForm.adminAmount} onChange={(e) => setEditForm((p) => ({ ...p, adminAmount: e.target.value }))} data-testid="input-edit-admin" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Cleaner Pay ($)</Label>
                      <Input type="number" step="0.01" value={editForm.cleanerAmount} onChange={(e) => setEditForm((p) => ({ ...p, cleanerAmount: e.target.value }))} data-testid="input-edit-cleaner" />
                    </div>
                    <div>
                      <Label className="text-xs">Partner Amount ($)</Label>
                      <Input type="number" step="0.01" value={editForm.partnerAmount} onChange={(e) => setEditForm((p) => ({ ...p, partnerAmount: e.target.value }))} data-testid="input-edit-partner" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} data-testid="input-edit-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Property Manager</Label>
                      <SearchableSelect
                        value={editForm.ownerUserId}
                        onValueChange={(v) => setEditForm((p) => ({ ...p, ownerUserId: v }))}
                        placeholder="None"
                        searchPlaceholder="Search PM..."
                        options={[
                          { value: "none", label: "None" },
                          ...typedUsers.filter(u => u.role === "owner").map(u => ({ value: String(u.id), label: `${u.fullName || u.username}${(u as any).isActive === false ? " (inactive)" : ""}` })),
                        ]}
                        className="w-full"
                        data-testid="select-edit-owner"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cleaner</Label>
                      <SearchableSelect
                        value={editForm.cleanerUserId}
                        onValueChange={(v) => setEditForm((p) => ({ ...p, cleanerUserId: v }))}
                        placeholder="None"
                        searchPlaceholder="Search cleaner..."
                        options={[
                          { value: "none", label: "None" },
                          ...typedUsers.filter(u => u.role === "cleaner" || ((u as any).additionalRoles as string[] | undefined ?? []).includes("cleaner")).map(u => ({ value: String(u.id), label: `${u.fullName || u.username}${(u as any).isActive === false ? " (inactive)" : ""}` })),
                        ]}
                        className="w-full"
                        data-testid="select-edit-cleaner"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Payment Method</Label>
                    <Select value={editForm.paymentMethod || "none"} onValueChange={(v) => setEditForm((p) => ({ ...p, paymentMethod: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="select-edit-payment">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                        <SelectItem value="airbnb_payout">Airbnb Payout</SelectItem>
                        <SelectItem value="venmo">Venmo</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="w-full" onClick={handleSaveEdit} disabled={updateRecord.isPending} data-testid="button-save-edit">
                    {updateRecord.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteConfirmRecord !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmRecord(null); }}
        title="Delete this financial record?"
        description={deleteConfirmRecord ? <>
          You're about to permanently delete a <strong>{deleteConfirmRecord.type.replace(/_/g, ' ')}</strong> financial record for <strong>{formatMoney(deleteConfirmRecord.totalAmount)}</strong>{deleteConfirmRecord.description ? <> ({deleteConfirmRecord.description})</> : null}.
          {deleteConfirmRecord.status === "paid" && <span className="block mt-2 text-destructive font-medium">This record is marked as paid. Deleting it removes the accounting record — there is no way to recover it.</span>}
          <span className="block mt-2">This cannot be undone.</span>
        </> : null}
        confirmLabel="Delete Record"
        pending={deleteRecord.isPending}
        cancelTestId="button-delete-record-cancel"
        confirmTestId="button-delete-record-confirm"
        onConfirm={async () => { if (deleteConfirmRecord) { await handleDeleteRecord(deleteConfirmRecord.id); setDeleteConfirmRecord(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmTierId !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmTierId(null); }}
        title="Delete this commission tier?"
        description="This will remove the per-bedroom commission tier from your commission settings. New cleaning jobs at that bedroom count will fall back to the default fee. Historical financial records are unaffected. This cannot be undone."
        confirmLabel="Delete Tier"
        pending={deleteTierMutation.isPending}
        onConfirm={() => { if (deleteConfirmTierId !== null) { deleteTierMutation.mutate(deleteConfirmTierId); setDeleteConfirmTierId(null); } }}
      />
    </AdminLayout>
  );
}
